/**
 * DOM ladder window math (SPEC §13.1; owner 2026-09-28 "needs to go up and down as much as I
 * need to set take profit and stop loss"). The ladder is virtual: it renders only the rows that
 * fit, starting at `top` (the price, in integer ticks, of the first visible row, prices falling
 * downwards). There is no fixed window around a centre price: scrolling moves `top` by whole
 * rows without limit, so any price can be reached. Auto-centre is an option that recentres only
 * when the last price leaves the middle band (§13.1: the middle 60 % ⚙) and pauses after the user
 * scrolls (5 s ⚙).
 *
 * Pure and integer-only (ticks are integers; nothing here is a float price).
 */

/** ⚙ SPEC §13.1: the last price may move within the middle 60 % before a recentre. */
export const AUTO_CENTER_BAND = 0.6;
/** ⚙ SPEC §13.1: auto-centre pauses this long after a manual scroll. */
export const AUTO_CENTER_PAUSE_MS = 5000;
/** Prices beyond this many ticks are refused (keeps every row a safe integer). */
export const LADDER_MAX_TICKS = 1_000_000_000_000;

/** Prices of the `count` visible rows from `top` downwards (one row per tick). */
export function ladderRows(top: number, count: number): number[] {
  const n = Math.max(0, Math.floor(count));
  return Array.from({ length: n }, (_, i) => top - i);
}

/** The `top` that puts `price` on the middle row of `count` rows. */
export function centeredTop(price: number, count: number): number {
  return price + Math.floor((Math.max(1, Math.floor(count)) - 1) / 2);
}

/** Clamp a `top` to the representable range (never a hard window: ±10¹² ticks). */
export function clampTop(top: number): number {
  return Math.max(-LADDER_MAX_TICKS, Math.min(LADDER_MAX_TICKS, Math.trunc(top)));
}

/** Scroll by `rows` (positive = towards lower prices, like a wheel turned down). */
export function scrollTop(top: number, rows: number): number {
  return clampTop(top - Math.trunc(rows));
}

/**
 * Wheel → whole rows. `deltaMode` 0 = pixels, 1 = lines, 2 = pages. The fractional remainder is
 * returned so smooth trackpads accumulate instead of losing small deltas.
 */
export function wheelRows(
  deltaY: number,
  deltaMode: number,
  rowPx: number,
  pageRows: number,
  carry = 0,
): { rows: number; carry: number } {
  const px = Math.max(1, rowPx);
  const inRows = deltaMode === 1 ? deltaY : deltaMode === 2 ? deltaY * Math.max(1, pageRows) : deltaY / px;
  const total = inRows + carry;
  const rows = Math.trunc(total);
  return { rows, carry: total - rows };
}

/** Is `price` inside the visible rows? */
export function isVisible(price: number, top: number, count: number): boolean {
  return price <= top && price > top - Math.floor(count);
}

/**
 * Auto-centre: the new `top` when the last price has left the middle band of the visible rows
 * (or is off-screen), else `top` unchanged. `band` is the fraction of rows the price may roam.
 */
export function autoCenterTop(top: number, count: number, last: number, band = AUTO_CENTER_BAND): number {
  const n = Math.max(1, Math.floor(count));
  const margin = Math.floor((n * (1 - band)) / 2);
  const hi = top - margin; // highest price still inside the band
  const lo = top - (n - 1) + margin; // lowest
  return last > hi || last < lo ? centeredTop(last, n) : top;
}

/** Rows to scroll while dragging an order near an edge: -1 near the top, +1 near the bottom. */
export function edgeScroll(pointerY: number, bodyTop: number, bodyBottom: number, rowPx: number): -1 | 0 | 1 {
  if (pointerY < bodyTop + rowPx) return -1;
  if (pointerY > bodyBottom - rowPx) return 1;
  return 0;
}
